import React from 'react';
import moment from 'moment';

import { PDFIcon } from '../components/reuse/SVG';
import { QUESTION_TYPES } from '../config/staffFormSchema';

// Turns a counselor record from `counselordetail` into the flat, questionKey-indexed shape both
// the staff application (to prefill its inputs) and the personnel detail page (to display them)
// work in. Shared so the two cannot drift: a field renamed here shows up wrong on both at once
// rather than silently on one.
export const hydrateValuesFromDetails = details => {
    const cd = details.counselorDetails || {};

    // Answers to director-added questions. Always an array, and — since the form was versioned —
    // nothing is ever dropped: each entry is joined against the form version this person actually
    // submitted on, so a question since deleted, or a form since reset, still renders here with
    // its original label and type. `inSnapshot: false` marks an answer whose question isn't part
    // of that submitted version (added afterward and filled in during a director edit, or a rare
    // pre-versioning orphan) rather than silently omitting it. This flattened map only needs
    // questionId -> answer for value lookups, so it ignores label/type/sectionKey/inSnapshot —
    // the personnel detail page reads those directly off counselorDetails.customAnswers to group
    // and label the "not in the snapshot" answers correctly.
    //
    // Spread first so the dedicated fields below win. The backend joins on questionKey alone
    // and does not filter locked questions, so a stale row for one that has since been locked
    // (userDescription) would otherwise overwrite the real value.
    const customAnswers = {};
    (details.customAnswers || []).forEach(entry => { customAnswers[entry.questionId] = entry.answer; });

    return {
        ...customAnswers,
        userTitle: details.userTitle,
        serving: cd.serving,
        firstName: cd.firstName,
        lastName: cd.lastName,
        email: details.email,
        phone: details.phone,
        streetAddress: cd.streetAddress,
        appartment: cd.appartment,
        city: cd.city,
        zipCode: cd.zipCode,
        state: cd.state,
        dob: details.dob,
        counselorProgram: details.counselorProgram,
        ageGroup: details.ageGroup,
        tshirtSize: cd.tshirtSize,
        shortsSize: cd.shortsSize,
        medicalvalues: cd.medicalvalues || [],
        allergyvalues: cd.allergyvalues || [],
        allergyDeadly: cd.allergyDeadly,
        medicalInsuranceCompany: cd.medicalInsuranceCompany,
        policyNumber: cd.policyNumber,
        group: cd.group,
        primaryPhysician: cd.primaryPhysician,
        physicianAddress: cd.physicianAddress,
        physicianPhone: cd.physicianPhone,
        medicationsTaken: cd.medicationsTaken,
        participantBringingOwnMedication: cd.participantBringingOwnMedication,
        allowDocumentCamper: cd.allowDocumentCamper,
        medicalDocument: cd.medicalDocument,
        userDescription: cd.userDescription !== undefined ? cd.userDescription : details.userDescription,
        readTrainingGuide: cd.readTrainingGuide === 1,
        signature: { signed: cd.signIsLegal === 1, image: cd.signatureImg },
    };
};

// Marks how a question in a merged form relates to the version the person submitted on.
// Attached by mergeSectionVersions and rendered by VersionHint, which QuestionField drops into
// each of its label variants — keep the wording and the tw- classes there, since
// tailwind.config.js does not scan src/utils.
export const QUESTION_VERSION_BADGE = {
    ADDED: 'added',     // on the camp's form now, but not when this person applied
    REMOVED: 'removed', // on the form when they applied, since deleted or hidden
};

/*
 * The union of the camp's current form and the version a person submitted on, for the counselor
 * who picks "show the updated form" after being told their version has changed.
 *
 * Current is the base and keeps its order and its `required` flags: that is the version the edit
 * will be validated against, so the backend really does demand them. A question that exists only
 * in their snapshot is appended to the section it originally sat in and forced **optional** —
 * this is load-bearing, not cosmetic. The backend requires only from the version being edited
 * (D3), so it will never demand one of these; leaving `required: true` on it would make
 * getSectionProgress count a question that can never be demanded, and handelSubmit blocks on
 * incomplete sections — trapping the person on a form they cannot submit.
 *
 * Membership is decided on `enabled`, not mere presence, so a question the director has since
 * hidden reads as removed rather than silently vanishing along with the answer to it. Both copies
 * then coexist — the hidden one from current, the enabled one from the snapshot — but SectionCard
 * filters on `enabled`, so exactly one of them renders and collectAnswers collects exactly one.
 */
export const mergeSectionVersions = (currentSections, snapshotSections) => {
    const current = currentSections || [];
    const snapshot = snapshotSections || [];

    const enabledKeys = sections => {
        const keys = new Set();
        sections.forEach(section => (section.questions || []).forEach(question => {
            if (question.enabled) keys.add(question.questionKey);
        }));
        return keys;
    };

    const currentKeys = enabledKeys(current);
    const snapshotKeys = enabledKeys(snapshot);

    // Snapshot-only questions, grouped by the section they sat in. Section membership never
    // changes — the Form Builder's move only reorders within one section — so the section a
    // question sat in then is still the right place for it now.
    const strandedBySection = {};
    snapshot.forEach(section => {
        (section.questions || []).forEach(question => {
            if (!question.enabled || currentKeys.has(question.questionKey)) return;
            const list = strandedBySection[section.sectionKey] || (strandedBySection[section.sectionKey] = []);
            list.push({ ...question, required: false, versionBadge: QUESTION_VERSION_BADGE.REMOVED });
        });
    });

    const merged = current.map(section => ({
        ...section,
        questions: [
            ...(section.questions || []).map(question => (
                !question.enabled || snapshotKeys.has(question.questionKey)
                    ? question
                    : { ...question, versionBadge: QUESTION_VERSION_BADGE.ADDED }
            )),
            ...(strandedBySection[section.sectionKey] || []),
        ],
    }));

    // A section that exists in the snapshot but not in the current form. The section list is
    // fixed in practice, so this is a safety net rather than a real case — but dropping it
    // silently would lose answers, which is the one outcome this whole screen exists to prevent.
    const currentSectionKeys = new Set(current.map(section => section.sectionKey));
    const strandedSections = snapshot
        .filter(section => !currentSectionKeys.has(section.sectionKey) && strandedBySection[section.sectionKey])
        .map(section => ({ ...section, questions: strandedBySection[section.sectionKey] }));

    return [...merged, ...strandedSections];
};

// Whether a hydrated value counts as filled in. Distinct from staffFormCompletion's `isAnswered`,
// which answers "did the applicant satisfy this required question?" — here 0 and false are real
// answers worth displaying, and only a genuinely absent value is empty.
export const hasValue = value => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Boolean(value.signed || value.image);
    return true;
};

const renderSignature = value => (value.image ? <img src={value.image} alt="Signature" /> : (value.signed ? 'Signed' : 'N/A'));

// Longest a stringified fallback value is shown at full length. A pre-versioning orphan (see
// below) can carry anything — a signature's base64 image is well over 10k characters — and an
// unbroken string that long is unreadable noise on the page, not useful data.
const MAX_FALLBACK_VALUE_LENGTH = 200;

// Renders one stored answer for display. The yes/no fields are the legacy 1/2 ints the backend
// stores, not booleans — a director-added `yesno` question stores the same pair.
export const formatAnswer = (question, value) => {
    if (!hasValue(value)) return 'N/A';

    switch (question.type) {
        case QUESTION_TYPES.YESNO:
            return Number(value) === 1 ? 'Yes' : 'No';
        case QUESTION_TYPES.CHECKBOX:
            return value ? 'Yes' : 'No';
        case QUESTION_TYPES.DATE:
            return moment.utc(value).format('MM/DD/YYYY');
        case QUESTION_TYPES.FILE:
            return <a href={value} target="_blank" rel="noreferrer" className="cm_pdf_icon m-0"><PDFIcon /></a>;
        case QUESTION_TYPES.SIGNATURE:
            return renderSignature(value);
        case QUESTION_TYPES.CHECKLIST:
            return Array.isArray(value) ? value.join(', ') : String(value);
        default:
            // A pre-versioning orphan (no version anywhere resolves it — see
            // getCustomAnswers' tier 4) always reports type: "text" regardless of what the
            // value actually was, since its real question definition is gone. A
            // {signed, image} shape is specifically recognizable — it is the one and only
            // shape a signature question ever stores — so it is worth rendering properly
            // rather than dumping its (often huge) base64 image as raw JSON text.
            if (value && typeof value === 'object' && !Array.isArray(value) && ('signed' in value || 'image' in value)) {
                return renderSignature(value);
            }
            if (Array.isArray(value)) return value.join(', ');
            // String() on a plain object yields the useless "[object Object]"; stringify it
            // instead so an unrecognized shape stays inspectable. Still capped — an unknown
            // object can carry its own oversized field (a document, a long free-text blob).
            const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
            return text.length > MAX_FALLBACK_VALUE_LENGTH ? `${text.slice(0, MAX_FALLBACK_VALUE_LENGTH)}…` : text;
    }
};
